'use client';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Select } from '@mantine/core';
import { Spinner } from '../../components/spinner/Spinner';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import secureLocalStorage from 'react-secure-storage';
import { useNavigate } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { config } from '../../Constants';
import {
  AddBonusModal,
  AddDepositModal,
  AddProfitModal,
} from '../modals/Modals';

export const UpdatePasswordForm = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));

  const formik = useFormik({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object({
      password: Yup.string().required(`Password is required`),
    }),
    onSubmit: async (values, { resetForm }) => {
      await updatePassword();
      resetForm();
    },
  });

  const updatePassword = async () => {
    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/auth/admin/update-password`,
        formik.values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
    } catch (error) {
      setLoading(false);
      toast.error(error.response.data.msg);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* NEW PASSWORD */}
      <div className='form-control'>
        <label
          htmlFor='password'
          className={
            formik.errors.password && formik.touched.password ? `error` : ``
          }
        >
          {formik.touched.password && formik.errors.password
            ? formik.errors.password
            : `New Password`}
        </label>
        <div className='password-cont'>
          <input
            type={showPassword ? `text` : `password`}
            id='password'
            name='password'
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {showPassword ? (
            <FaEye
              onClick={() => {
                setShowPassword(false);
              }}
            />
          ) : (
            <FaEyeSlash
              onClick={() => {
                setShowPassword(true);
              }}
            />
          )}
        </div>
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Password
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdateWhatsappNumberForm = ({ data }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      number: '',
    },
    validationSchema: Yup.object({
      number: Yup.string().required(`Whatsapp number is required`),
    }),
    onSubmit: async (values, { resetForm }) => {
      await updateNumber();
      resetForm();
    },
  });

  const updateNumber = async () => {
    try {
      setLoading(true);
      const { data: info } = await axios.patch(
        `${baseURL}/whatsapp/${data?._id}`,
        formik.values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setLoading(false);
      toast.success(info.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      toast.error(error.response.data.msg);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* NEW NUMBER */}
      <div className='form-control'>
        <label
          htmlFor='whatsapp'
          className={
            formik.errors.number && formik.touched.number ? `error` : ``
          }
        >
          {formik.touched.number && formik.errors.number
            ? formik.errors.number
            : `New WhatsApp Number`}
        </label>
        <input
          type='text'
          id='number'
          name='number'
          value={formik.values.number}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={data?.number ?? 0}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Number
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdatePersonalInfoForm = ({
  email,
  fullName,
  userId,
  phone,
  country,
  address,
  investmentGoal,
  gender,
}) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      fullName: fullName,
      email: email,
      phone: phone,
      country: country,
      address: address,
      investmentGoal: investmentGoal,
      gender: gender,
    },
    onSubmit: async (value, { resetForm }) => {
      updateInfo();
      resetForm();
    },
  });

  const updateInfo = async () => {
    const { email, fullName, phone, country, address, investmentGoal, gender } =
      formik.values;

    const formData = new FormData();
    if (email) {
      formData.append(`email`, email);
    }
    if (fullName) {
      formData.append(`fullName`, fullName);
    }
    if (phone) {
      formData.append(`phone`, phone);
    }
    if (country) {
      formData.append(`country`, country);
    }
    if (address) {
      formData.append(`address`, address);
    }
    if (investmentGoal) {
      formData.append(`investmentGoal`, investmentGoal);
    }
    if (gender) {
      formData.append(`gender`, gender);
    }

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/users/${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* NAME & EMAIL */}
      <div className='form-row'>
        {/* fullname */}
        <div className='form-control'>
          <label
            htmlFor='fullName'
            className={
              formik.errors.fullName && formik.touched.fullName ? `error` : ``
            }
          >
            {formik.touched.fullName && formik.errors.fullName
              ? formik.errors.fullName
              : `Fullname`}
          </label>
          <input
            type='fullName'
            id='fullName'
            value={formik.values.fullName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={fullName}
          />
        </div>

        {/* email */}
        <div className='form-control'>
          <label
            htmlFor='email'
            className={
              formik.errors.email && formik.touched.email ? `error` : ``
            }
          >
            {formik.touched.email && formik.errors.email
              ? formik.errors.email
              : `Email`}
          </label>
          <input
            type='email'
            id='email'
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={email}
          />
        </div>
      </div>

      {/* PHONE & COUNTRY */}
      <div className='form-row'>
        {/* phone */}
        <div className='form-control'>
          <label
            htmlFor='phone'
            className={
              formik.errors.phone && formik.touched.phone ? `error` : ``
            }
          >
            {formik.touched.phone && formik.errors.phone
              ? formik.errors.phone
              : `Phone Number`}
          </label>
          <input
            type='phone'
            id='phone'
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={phone}
          />
        </div>

        {/* country */}
        <div className='form-control'>
          <label
            htmlFor='country'
            className={
              formik.errors.country && formik.touched.country ? `error` : ``
            }
          >
            {formik.touched.country && formik.errors.country
              ? formik.errors.country
              : `Country`}
          </label>
          <input
            type='text'
            id='country'
            value={formik.values.country}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={country}
          />
        </div>
      </div>

      {/* ADDRESS & INVESTMENT GOAL */}
      <div className='form-row'>
        {/* address */}
        <div className='form-control'>
          <label
            htmlFor='address'
            className={
              formik.errors.address && formik.touched.address ? `error` : ``
            }
          >
            {formik.touched.address && formik.errors.address
              ? formik.errors.address
              : `Address`}
          </label>
          <input
            type='text'
            id='address'
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={address}
          />
        </div>

        {/* investment goal */}
        <div className='form-control'>
          <label
            htmlFor='investmentGoal'
            className={
              formik.errors.investmentGoal && formik.touched.investmentGoal
                ? `error`
                : ``
            }
          >
            {formik.touched.investmentGoal && formik.errors.investmentGoal
              ? formik.errors.investmentGoal
              : `Investment Goal`}
          </label>
          <input
            type='text'
            id='investmentGoal'
            value={formik.values.investmentGoal}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={investmentGoal}
          />
        </div>
      </div>

      {/* GENDER &  */}
      <div className='form-row'>
        {/* gender */}
        <div className='form-control'>
          <label
            htmlFor='gender'
            className={
              formik.errors.gender && formik.touched.gender ? `error` : ``
            }
          >
            {formik.touched.gender && formik.errors.gender
              ? formik.errors.gender
              : `Gender`}
          </label>
          <input
            type='text'
            id='gender'
            value={formik.values.gender}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={gender}
          />
        </div>
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Info
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdateAccountBalanceForm = ({ userId, accountBalance }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedModal, setSelectedModal] = useState(null);

  console.log(accountBalance);

  const openModal = (modalName) => {
    setSelectedModal(modalName);
    open();
  };

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      withdrawalCharges: ``,
      totalEquity: ``,
      signalStrength: ``,
      bitcoin: ``,
      xrp: ``,
    },
    validationSchema: Yup.object({
      withdrawalCharges: Yup.string()
        .matches(/^[0-9]+$/, 'Numbers only')
        .max(100, 'Max 100'),
      totalEquity: Yup.string().matches(/^[0-9]+$/, 'Numbers only'),
      bitcoin: Yup.string().matches(/^[0-9]*\.?[0-9]+$/, 'Numbers only'),
      xrp: Yup.string().matches(/^[0-9]*\.?[0-9]+$/, 'Numbers only'),
    }),
    onSubmit: async (values, { resetForm }) => {
      await UpdateInfo();
      resetForm();
    },
  });

  const UpdateInfo = async () => {
    const { withdrawalCharges, totalEquity, signalStrength, bitcoin, xrp } =
      formik.values;

    const formData = new FormData();
    if (withdrawalCharges) {
      formData.append(`withdrawalCharges`, withdrawalCharges);
    }
    if (totalEquity) {
      formData.append(`totalEquity`, totalEquity);
    }
    if (signalStrength) {
      formData.append(`signalStrength`, signalStrength);
    }
    if (bitcoin) {
      formData.append(`bitcoin`, bitcoin);
    }
    if (xrp) {
      formData.append(`xrp`, xrp);
    }

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/account-balance/${accountBalance._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* CRYPTO BALANCES */}
      <div className='form-row'>
        {/* bitcoin */}
        <div className='form-control'>
          <label htmlFor='bitcoin'>Bitcoin</label>
          <input
            type='number'
            id='bitcoin'
            name='bitcoin'
            value={formik.values.bitcoin}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={accountBalance.bitcoin || '0'}
            step='0.00000001'
          />
          {formik.touched.bitcoin && formik.errors.bitcoin ? (
            <div className='error'>{formik.errors.bitcoin}</div>
          ) : null}
        </div>

        {/* xrp */}
        <div className='form-control'>
          <label htmlFor='xrp'>XRP</label>
          <input
            type='number'
            id='xrp'
            name='xrp'
            value={formik.values.xrp}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={accountBalance.xrp || '0'}
            step='0.000001'
          />
          {formik.touched.xrp && formik.errors.xrp ? (
            <div className='error'>{formik.errors.xrp}</div>
          ) : null}
        </div>
      </div>

      {/* profits */}
      <div className='form-control'>
        <label htmlFor='profits'>Total Profits</label>
        <div className='input-btn'>
          <input
            type='profits'
            id='profits'
            value={`$${accountBalance.profits}`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            readOnly
          />
          <button
            className='primary'
            type='button'
            onClick={() => {
              openModal(`add-profit`);
            }}
          >
            Add
          </button>
        </div>
      </div>

      {/* DEPOSITS & WITHDRAWALS */}
      <div className='form-row'>
        {/* deposits */}
        <div className='form-control'>
          <label htmlFor='deposits'>Total Deposits</label>
          <div className='input-btn'>
            <input
              type='deposits'
              id='deposits'
              value={`$${accountBalance.deposits}`}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              readOnly
            />
            <button
              className='primary'
              type='button'
              onClick={() => {
                openModal(`add-deposit`);
              }}
            >
              Add
            </button>
          </div>
        </div>

        {/* withdrawals */}
        <div className='form-control'>
          <label htmlFor='withdrawals'>Total Withdrawals</label>
          <input
            type='withdrawals'
            id='withdrawals'
            value={`$${accountBalance.withdrawals}`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            readOnly
          />
        </div>
      </div>

      {/* PENDING DEPOSITS & WITHDRAWALS */}
      <div className='form-row'>
        {/* pending deposits */}
        <div className='form-control'>
          <label htmlFor='pendingDeposits'>Pending Deposits</label>
          <input
            type='pendingDeposits'
            id='pendingDeposits'
            value={`$${accountBalance.pendingDeposits}`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            readOnly
          />
        </div>

        {/* pending withdrawals */}
        <div className='form-control'>
          <label htmlFor='pendingWithdrawals'>Pending Withdrawals</label>
          <input
            type='pendingWithdrawals'
            id='pendingWithdrawals'
            value={`$${accountBalance.pendingWithdrawals}`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            readOnly
          />
        </div>
      </div>

      {/* BONUS & WITHDRAWAL CHARGES */}
      <div className='form-row'>
        {/* bonus */}
        <div className='form-control'>
          <label htmlFor='bonus'>Bonus</label>
          <div className='input-btn'>
            <input
              type='bonus'
              id='bonus'
              value={`$${accountBalance.bonus}`}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              readOnly
            />
            <button
              className='primary'
              type='button'
              onClick={() => {
                openModal(`add-bonus`);
              }}
            >
              Add
            </button>
          </div>
        </div>

        {/* withdrawal charges */}
        <div className='form-control'>
          <label
            htmlFor='withdrawalCharges'
            className={
              formik.errors.withdrawalCharges &&
              formik.touched.withdrawalCharges
                ? `error`
                : ``
            }
          >
            {formik.touched.withdrawalCharges && formik.errors.withdrawalCharges
              ? formik.errors.withdrawalCharges
              : `Withdrawal Charges`}
          </label>
          <input
            type='withdrawalCharges'
            id='withdrawalCharges'
            value={formik.values.withdrawalCharges}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={`${accountBalance.withdrawalCharges}%`}
          />
        </div>
      </div>

      {/* TOTAL EQUITY & SIGNAL STRENGHT */}
      <div className='form-row'>
        {/* total equity */}
        <div className='form-control'>
          <label
            htmlFor='totalEquity'
            className={
              formik.errors.totalEquity && formik.touched.totalEquity
                ? `error`
                : ``
            }
          >
            {formik.touched.totalEquity && formik.errors.totalEquity
              ? formik.errors.totalEquity
              : `Total Equity`}
          </label>
          <input
            type='totalEquity'
            id='totalEquity'
            value={formik.values.totalEquity}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={`$${accountBalance.totalEquity}`}
          />
        </div>
        {/* signal strength */}
        <div className='form-control'>
          <label
            htmlFor='signalStrength'
            className={
              formik.errors.signalStrength && formik.touched.signalStrength
                ? `error`
                : ``
            }
          >
            {formik.touched.signalStrength && formik.errors.signalStrength
              ? formik.errors.signalStrength
              : `Signal Strenght`}
          </label>
          <input
            type='signalStrength'
            id='signalStrength'
            value={formik.values.signalStrength}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={`${accountBalance.signalStrength}%`}
          />
        </div>
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Info
          {loading ? <Spinner /> : null}
        </button>
      </div>

      {selectedModal === `add-profit` && (
        <AddProfitModal close={close} opened={opened} userId={userId} />
      )}
      {selectedModal === `add-deposit` && (
        <AddDepositModal close={close} opened={opened} userId={userId} />
      )}
      {selectedModal === `add-bonus` && (
        <AddBonusModal close={close} opened={opened} userId={userId} />
      )}
    </form>
  );
};

export const UpdateNotificationForm = ({ notification, userId }) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(notification.title);
  const [content, setContent] = useState(``);
  const [persistent, setPersistent] = useState(notification.persistent);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    UpdateNotification();
  };

  const UpdateNotification = async () => {
    const formData = new FormData();
    formData.append(`title`, title);
    formData.append(`content`, content);
    formData.append(`persistent`, persistent === `true` ? true : false);
    formData.append(`seen`, false);

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/users/${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* title */}
      <div className='form-control'>
        <label htmlFor='title'>Notification title</label>
        <input
          type='text'
          name='title'
          id='title'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      {/* conntent */}
      <div className='form-control'>
        <label htmlFor='content'>Notification Content</label>
        <CKEditor
          editor={ClassicEditor}
          data={notification?.content}
          onChange={(event, editor) => {
            if (!editor.getData()) {
              setContent(``);
            }
            setContent(editor.getData());
          }}
        />
      </div>

      <div className='form-control'>
        <div className='select-status'>
          <Select
            label='Will this notification be persistent?'
            required
            name='persistent'
            placeholder={
              persistent
                ? `This notification will be persistent`
                : `This notification will not be persistent`
            }
            onChange={(value) => setPersistent(value)}
            data={[
              { value: 'false', label: 'No' },
              { value: 'true', label: 'Yes' },
            ]}
          />
        </div>
      </div>

      <div className='form-control'>
        <label htmlFor='title'>Seen Status</label>
        <input
          type='text'
          name='title'
          id='title'
          value={
            notification.seen
              ? `This notification has been seen`
              : `This notification has not been seen`
          }
          contentEditable={false}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Notification
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdateDepositStatusForm = ({ depositId, status }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [newStatus, setNewStatus] = useState(``);

  const updateDepositStatus = async () => {
    const formData = new FormData();
    if (newStatus) {
      formData.append(`status`, newStatus);
    }

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/deposits/admin/${depositId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateDepositStatus();
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* crypto type */}
      <div className='select-status'>
        <Select
          label='Choose the new deposit status'
          required
          name='type'
          placeholder={status}
          onChange={(value) => setNewStatus(value)}
          data={[
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Status
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdateWithdrawalStatusForm = ({ withdrawalId, status }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [newStatus, setNewStatus] = useState(``);

  const updateWithdrawalStatus = async () => {
    const formData = new FormData();
    if (newStatus) {
      formData.append(`status`, newStatus);
    }

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/withdrawals/admin/${withdrawalId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateWithdrawalStatus();
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* crypto type */}
      <div className='select-status'>
        <Select
          label='Choose the new withdrawal status'
          required
          name='type'
          placeholder={status}
          onChange={(value) => setNewStatus(value)}
          data={[
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Status
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdateWalletForm = ({ data }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const baseURL = config.baseURL;

  const formik = useFormik({
    initialValues: {
      address: '',
    },
    onSubmit: async (_, { resetForm }) => {
      await updateWallets();
      resetForm();
    },
  });

  const updateWallets = async () => {
    const { address } = formik.values;

    try {
      setLoading(true);
      await axios.patch(
        `${baseURL}/admin-wallets/${data._id}`,
        { address },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(`Wallet Updated`);
      navigate(0);
    } catch (error) {
      toast.error(error.response.data.msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* address */}
      <div className='form-control'>
        <label
          htmlFor={data?.walletName}
          className={
            formik.errors.address && formik.touched.address ? `error` : ``
          }
        >
          {formik.touched.address && formik.errors.address
            ? formik.errors.address
            : `${data.walletName} Address`}
        </label>
        <input
          type='text'
          id='address'
          value={formik.values.address}
          name='address'
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={data.address}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Address
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdateKYCStatusForm = ({ kycId, status }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [newStatus, setNewStatus] = useState(``);

  const updateDepositStatus = async () => {
    const formData = new FormData();
    if (newStatus) {
      formData.append(`status`, newStatus);
    }

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/kyc/admin/${kycId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateDepositStatus();
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* crypto type */}
      <div className='select-status'>
        <Select
          label='Choose the new deposit status'
          required
          name='type'
          placeholder={status}
          onChange={(value) => setNewStatus(value)}
          data={[
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Status
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const SendEmailForm = ({ email }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [message, setMessage] = useState(``);

  const formik = useFormik({
    initialValues: {
      subject: ``,
    },
    validationSchema: Yup.object({
      subject: Yup.string().required(`Email subject is required`),
    }),
    onSubmit: async (values, { resetForm }) => {
      await sendEmail();
      resetForm();
    },
  });

  const sendEmail = async () => {
    const { subject } = formik.values;

    const formData = new FormData();
    if (message) {
      formData.append(`message`, message);
    }
    if (subject) {
      formData.append(`subject`, subject);
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${baseURL}/send-emails/${email}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
      toast.error(error.response.data.msg);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* subject */}
      <div className='form-control'>
        <label
          htmlFor='subject'
          className={
            formik.errors.subject && formik.touched.subject ? `error` : ``
          }
        >
          {formik.touched.subject && formik.errors.subject
            ? formik.errors.subject
            : `Email Subject`}
        </label>
        <input
          type='text'
          name='subject'
          id='subject'
          value={formik.values.subject}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder='Enter Email Subject here'
        />
      </div>

      {/* message */}
      <div className='form-control'>
        <label
          htmlFor='message'
          className={
            formik.errors.message && formik.touched.message ? `error` : ``
          }
        >
          {formik.touched.message && formik.errors.message
            ? formik.errors.message
            : `Message`}
        </label>
        <CKEditor
          editor={ClassicEditor}
          data=''
          onChange={(event, editor) => {
            setMessage(editor.getData());
          }}
          className='editor'
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Send Email
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const SendBulkEmailsForm = () => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [message, setMessage] = useState(``);

  const formik = useFormik({
    initialValues: {
      subject: ``,
    },
    validationSchema: Yup.object({
      subject: Yup.string().required(`Email subject is required`),
    }),
    onSubmit: async (values, { resetForm }) => {
      await sendEmail();
      resetForm();
    },
  });

  const sendEmail = async () => {
    const { subject } = formik.values;

    const formData = new FormData();
    if (message) {
      formData.append(`message`, message);
    }
    if (subject) {
      formData.append(`subject`, subject);
    }

    try {
      setLoading(true);
      const { data } = await axios.post(`${baseURL}/send-emails/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
      toast.error(error.response.data.msg);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* subject */}
      <div className='form-control'>
        <label
          htmlFor='subject'
          className={
            formik.errors.subject && formik.touched.subject ? `error` : ``
          }
        >
          {formik.touched.subject && formik.errors.subject
            ? formik.errors.subject
            : `Email Subject`}
        </label>
        <input
          type='text'
          name='subject'
          id='subject'
          value={formik.values.subject}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder='Enter Email Subject here'
        />
      </div>

      {/* message */}
      <div className='form-control'>
        <label
          htmlFor='message'
          className={
            formik.errors.message && formik.touched.message ? `error` : ``
          }
        >
          {formik.touched.message && formik.errors.message
            ? formik.errors.message
            : `Message`}
        </label>
        <CKEditor
          editor={ClassicEditor}
          data=''
          onChange={(event, editor) => {
            setMessage(editor.getData());
          }}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Send Email
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const DeleteUserForm = ({ id }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const [opened, { open, close }] = useDisclosure(false);

  const navigate = useNavigate();

  const deleteUser = async () => {
    try {
      setLoading(true);
      const { data } = await axios.delete(`${baseURL}/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setLoading(false);
      toast.success(data.msg);
      close();
      navigate(`/dashboard/users`);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
      toast.error(error.response.data.msg);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    open();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className='btn'>
        <button className='red' type='submit'>
          Delete User
        </button>
      </div>

      <Modal
        opened={opened}
        onClose={close}
        centered
        size='lg'
        className='delete-modal'
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <div className='warning'>
          <h2>Are you sure you want to delete this user?</h2>
        </div>

        <div className='btns'>
          <button className='primary' onClick={deleteUser}>
            Yes, I am sure {loading ? <Spinner /> : null}
          </button>
          <button className='grey' onClick={close}>
            Cancel
          </button>
        </div>
      </Modal>
    </form>
  );
};

export const UpdateUserWithdrawalStatusForm = ({ userId, userCanWithdraw }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [newStatus, setNewStatus] = useState(``);

  const updateDepositStatus = async () => {
    const value = newStatus === `true` ? true : false;

    const formData = new FormData();
    if (newStatus) {
      formData.append(`userCanWithdraw`, value);
    }

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/users/${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateDepositStatus();
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* crypto type */}
      <div className='form-control'>
        <Select
          label='Choose the new deposit status'
          required
          name='type'
          className='withdrawal-select'
          placeholder={
            userCanWithdraw
              ? `User can withdraw now`
              : `User can't withdraw right now`
          }
          onChange={(value) => setNewStatus(value)}
          data={[
            { value: 'false', label: 'Do not allow user to withdraw' },
            { value: 'true', label: 'Allow user to withdraw' },
          ]}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Withdrawal Status
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdateUserVerificationStatusForm = ({ userId, isVerified }) => {
  const [loading, setLoading] = useState(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [newStatus, setNewStatus] = useState(``);

  const updateDepositStatus = async () => {
    const value = newStatus === `true` ? true : false;

    const formData = new FormData();
    if (newStatus) {
      formData.append(`isVerified`, value);
    }

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/users/${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateDepositStatus();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className='form-control'>
        <Select
          label='Choose the new deposit status'
          required
          name='type'
          className='withdrawal-select'
          value={isVerified ? `User is verified` : `User is unverified`}
          placeholder={
            isVerified ? `User is verified already` : `User is unverified`
          }
          onChange={(value) => {
            if (!value) {
              setNewStatus();
            }
            setNewStatus(value);
          }}
          data={[
            { value: 'false', label: 'Unverify this user' },
            { value: 'true', label: 'Verify this user' },
          ]}
        />
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Update Verification Status
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const AddUserForm = ({ close }) => {
  const baseURL = config.baseURL;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      fullName: ``,
      email: ``,
      phone: ``,
      country: ``,
      referredBy: ``,
      password: ``,
    },
    onSubmit: async (value, { resetForm }) => {
      await updateInfo();
      close();
    },
  });

  const updateInfo = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${baseURL}/auth/register/admin-reg`,
        formik.values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* NAME & EMAIL */}
      <div className='form-row'>
        {/* fullname */}
        <div className='form-control'>
          <label
            htmlFor='fullName'
            className={
              formik.errors.fullName && formik.touched.fullName ? `error` : ``
            }
          >
            {formik.touched.fullName && formik.errors.fullName
              ? formik.errors.fullName
              : `Fullname`}
          </label>
          <input
            type='fullName'
            id='fullName'
            value={formik.values.fullName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        {/* email */}
        <div className='form-control'>
          <label
            htmlFor='email'
            className={
              formik.errors.email && formik.touched.email ? `error` : ``
            }
          >
            {formik.touched.email && formik.errors.email
              ? formik.errors.email
              : `Email`}
          </label>
          <input
            type='email'
            id='email'
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      </div>

      {/* PHONE & COUNTRY */}
      <div className='form-row'>
        {/* phone */}
        <div className='form-control'>
          <label
            htmlFor='phone'
            className={
              formik.errors.phone && formik.touched.phone ? `error` : ``
            }
          >
            {formik.touched.phone && formik.errors.phone
              ? formik.errors.phone
              : `Phone Number`}
          </label>
          <input
            type='phone'
            id='phone'
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        {/* country */}
        <div className='form-control'>
          <label
            htmlFor='country'
            className={
              formik.errors.country && formik.touched.country ? `error` : ``
            }
          >
            {formik.touched.country && formik.errors.country
              ? formik.errors.country
              : `Country`}
          </label>
          <input
            type='text'
            id='country'
            value={formik.values.country}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      </div>

      {/* REFERRED BY & PASSWORD */}
      <div className='form-row'>
        {/* referred by */}
        <div className='form-control'>
          <label
            htmlFor='referredBy'
            className={
              formik.errors.referredBy && formik.touched.referredBy
                ? `error`
                : ``
            }
          >
            {formik.touched.referredBy && formik.errors.referredBy
              ? formik.errors.referredBy
              : `Referred By`}
          </label>
          <input
            type='text'
            id='referredBy'
            value={formik.values.referredBy}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        {/* password */}
        <div className='form-control'>
          <label
            htmlFor='password'
            className={
              formik.errors.password && formik.touched.password ? `error` : ``
            }
          >
            {formik.touched.password && formik.errors.password
              ? formik.errors.password
              : `Password`}
          </label>
          <div className='password-cont'>
            <input
              type={showPassword ? `text` : `password`}
              name='password'
              id='password'
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {showPassword ? (
              <FaEye
                onClick={() => {
                  setShowPassword(false);
                }}
              />
            ) : (
              <FaEyeSlash
                onClick={() => {
                  setShowPassword(true);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Create User
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const AddProfitForm = ({ close, userId }) => {
  const baseURL = config.baseURL;
  const [loading, setLoading] = useState(false);
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [investments, setInvestments] = useState([]);

  const formik = useFormik({
    initialValues: {
      amount: 0,
      investment: ``,
    },
    onSubmit: async () => {
      await addProfit();
      close();
    },
  });

  useEffect(() => {
    const fetchInvestments = async () => {
      try {
        const { data } = await axios.get(`${baseURL}/investments/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setInvestments(data);
      } catch (error) {}
    };
    fetchInvestments();
    // eslint-disable-next-line
  }, []);

  const addProfit = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${baseURL}/profits?userId=${userId}`,
        formik.values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* AMOUNT & INVESTMENTS */}
      <div className='form-row'>
        {/* amount */}
        <div className='form-control'>
          <label htmlFor='amount'>Amount</label>
          <input
            type='amount'
            id='amount'
            value={formik.values.amount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        {/* investment */}
        <div className='form-control'>
          <label htmlFor='investment'>Investment Plan</label>
          {investments.length > 0 ? (
            <select
              name='investment'
              id='investment'
              value={formik.values.investment}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value='' hidden label='Select an investment' />{' '}
              {investments.map((investment) => (
                <option key={investment._id} value={investment.investmentPlan}>
                  {investment.investmentPlan}
                </option>
              ))}
            </select>
          ) : (
            <input
              type='text'
              name='investment'
              id='investment'
              placeholder='Enter investment plan name'
              value={formik.values.investment}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          )}
        </div>
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Add Profit
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const AddDepositForm = ({ close, userId }) => {
  const baseURL = config.baseURL;
  const [loading, setLoading] = useState(false);
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);

  const formik = useFormik({
    initialValues: {
      amount: 0,
      asset: ``,
      address: ``,
    },
    onSubmit: async () => {
      await addDeposit();
      close();
    },
  });

  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const { data } = await axios.get(`${baseURL}/admin-wallets`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setWallets(data);
      } catch (error) {}
    };
    fetchWallets();
    // eslint-disable-next-line
  }, []);

  const addDeposit = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${baseURL}/deposits/admin?userId=${userId}`,
        formik.values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* AMOUNT & ASSETS */}
      <div className='form-row'>
        {/* amount */}
        <div className='form-control'>
          <label htmlFor='amount'>Amount</label>
          <input
            type='number'
            id='amount'
            value={formik.values.amount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        {/* asset */}
        <div className='form-control'>
          <label htmlFor='asset'>Assets</label>
          <select
            name='asset'
            id='asset'
            value={formik.values.asset}
            onChange={(e) => {
              const selectedAsset = e.target.value;
              formik.setFieldValue('asset', selectedAsset);
              const selectedWallet = wallets.find(
                (wallet) => wallet.walletName === selectedAsset,
              );
              formik.setFieldValue(
                'address',
                selectedWallet ? selectedWallet.address : '',
              );
            }}
            onBlur={formik.handleBlur}
          >
            <option value='' hidden label='Select an asset' />
            {wallets.map((wallet) => (
              <option key={wallet._id} value={wallet.walletName}>
                {wallet.walletName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Add Deposit
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const AddBonusForm = ({ close, userId }) => {
  const baseURL = config.baseURL;
  const [loading, setLoading] = useState(false);
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      amount: 0,
      type: ``,
      narration: ``,
    },
    onSubmit: async () => {
      await addBonus();
      close();
    },
  });

  const addBonus = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${baseURL}/bonus?userId=${userId}`,
        formik.values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg);
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error(error.message);
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* AMOUNT & TYPE */}
      <div className='form-row'>
        {/* amount */}
        <div className='form-control'>
          <label htmlFor='amount'>Amount</label>
          <input
            type='number'
            id='amount'
            value={formik.values.amount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        {/* type */}
        <div className='form-control'>
          <label htmlFor='type'>Type</label>
          <input
            type='text'
            id='type'
            value={formik.values.type}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      </div>

      {/* NARRATION */}
      <div className='form-row'>
        {/* narration */}
        <div className='form-control'>
          <label htmlFor='narration'>Narration</label>
          <input
            type='text'
            id='narration'
            value={formik.values.narration}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      </div>

      <div className='btn'>
        <button className='primary' type='submit'>
          Add Bonus
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const AddTradingPlanForm = ({ close }) => {
  const baseURL = config.baseURL;
  const [loading, setLoading] = useState(false);
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      name: '',
      duration: '',
      minDeposit: '',
      maxDeposit: '',
      minReturns: '',
      maxReturns: '',
      profit: '',
    },
    onSubmit: async () => {
      await addTradingPlan();
      // close();
    },
  });

  const addTradingPlan = async () => {
    const requestBody = {
      name: formik.values.name,
      duration: formik.values.duration,
      minDeposit: Number(formik.values.minDeposit),
      maxDeposit: Number(formik.values.maxDeposit),
      minReturns: Number(formik.values.minReturns),
      maxReturns: Number(formik.values.maxReturns),
      profit: formik.values.profit,
    };

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${baseURL}/trading-plans`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg || 'Trading plan created successfully');
      navigate(0);
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg || 'Failed to create trading plan');
      } else {
        toast.error(error.message || 'Network error occurred');
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* PLAN NAME & DURATION */}
      <div className='form-row'>
        {/* Plan Name */}
        <div className='form-control'>
          <label htmlFor='name'>Plan Name</label>
          <input
            type='text'
            id='name'
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter plan name'
          />
        </div>

        {/* Duration */}
        <div className='form-control'>
          <label htmlFor='duration'>Duration (days)</label>
          <input
            type='text'
            id='duration'
            value={formik.values.duration}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter duration e.g 1 Month'
          />
        </div>
      </div>

      {/* MIN DEPOSIT & MAX DEPOSIT */}
      <div className='form-row'>
        {/* Minimum Deposit */}
        <div className='form-control'>
          <label htmlFor='minDeposit'>Minimum Deposit ($)</label>
          <input
            type='number'
            id='minDeposit'
            value={formik.values.minDeposit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter minimum deposit'
            min='0'
          />
        </div>

        {/* Maximum Deposit */}
        <div className='form-control'>
          <label htmlFor='maxDeposit'>Maximum Deposit ($)</label>
          <input
            type='number'
            id='maxDeposit'
            value={formik.values.maxDeposit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter maximum deposit'
            min='0'
          />
        </div>
      </div>

      {/* MIN RETURNS & MAX RETURNS */}
      <div className='form-row'>
        {/* Minimum Returns */}
        <div className='form-control'>
          <label htmlFor='minReturns'>Minimum Returns (%)</label>
          <input
            type='number'
            id='minReturns'
            value={formik.values.minReturns}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter minimum returns percentage'
            min='0'
          />
        </div>

        {/* Maximum Returns */}
        <div className='form-control'>
          <label htmlFor='maxReturns'>Maximum Returns (%)</label>
          <input
            type='number'
            id='maxReturns'
            value={formik.values.maxReturns}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter maximum returns percentage'
            min='0'
          />
        </div>
      </div>

      {/* PROFIT */}
      <div className='form-row'>
        <div className='form-control'>
          <label htmlFor='profit'>Profit</label>
          <input
            type='text'
            id='profit'
            value={formik.values.profit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter profit e.g 300% Monthly'
          />
        </div>
      </div>

      <div className='btn'>
        <button className='primary' type='submit' disabled={loading}>
          Create Trading Plan
          {loading ? <Spinner /> : null}
        </button>
      </div>
    </form>
  );
};

export const UpdateTradingPlanForm = ({
  name,
  duration,
  minDeposit,
  maxDeposit,
  roi,
  planId,
}) => {
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const baseURL = config.baseURL;

  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      name: name || '',
      duration: duration || '',
      minDeposit: minDeposit || '',
      maxDeposit: maxDeposit || '',
      roi: roi || '',
    },
    onSubmit: async (values, { resetForm }) => {
      await updateTradingPlan(values);
      resetForm();
    },
  });

  const updateTradingPlan = async (values) => {
    const { name, duration, minDeposit, maxDeposit, roi } = values;

    const requestBody = {
      name,
      duration: duration,
      minDeposit: Number(minDeposit),
      maxDeposit: Number(maxDeposit),
      roi: roi,
    };

    try {
      setLoading(true);
      const { data } = await axios.patch(
        `${baseURL}/trading-plans/${planId}`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setLoading(false);
      toast.success(data.msg || 'Trading plan updated successfully');
      navigate(0); // Refresh the page
    } catch (error) {
      setLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg || 'Failed to update trading plan');
      } else {
        toast.error(error.message || 'Network error occurred');
      }
    }
  };

  const handleDeleteClick = () => {
    openDeleteModal();
  };

  const handleDeleteConfirm = async () => {
    closeDeleteModal();
    await deleteTradingPlan();
  };

  const deleteTradingPlan = async (values) => {
    try {
      setDeleteLoading(true);
      const { data } = await axios.delete(
        `${baseURL}/trading-plans/${planId}`,

        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setDeleteLoading(false);
      toast.success(data.msg || 'Trading plan deleted successfully');
      navigate('/dashboard/trading-plans');
    } catch (error) {
      setDeleteLoading(false);
      if (error.response) {
        toast.error(error.response.data.msg || 'Failed to delete trading plan');
      } else {
        toast.error(error.message || 'Network error occurred');
      }
    }
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        {/* PLAN NAME & DURATION */}
        <div className='form-row'>
          {/* Plan Name */}
          <div className='form-control'>
            <label htmlFor='name'>Plan Name</label>
            <input
              type='text'
              id='name'
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder='Enter plan name'
            />
          </div>

          {/* Duration */}
          <div className='form-control'>
            <label htmlFor='duration'>Duration (days)</label>
            <input
              type='text'
              id='duration'
              value={formik.values.duration}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
        </div>

        {/* MIN DEPOSIT & MAX DEPOSIT */}
        <div className='form-row'>
          {/* Minimum Deposit */}
          <div className='form-control'>
            <label htmlFor='minDeposit'>Minimum Deposit ($)</label>
            <input
              type='number'
              id='minDeposit'
              value={formik.values.minDeposit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder='Enter minimum deposit'
              min='0'
              step='0.01'
            />
          </div>

          {/* Maximum Deposit */}
          <div className='form-control'>
            <label htmlFor='maxDeposit'>Maximum Deposit ($)</label>
            <input
              type='number'
              id='maxDeposit'
              value={formik.values.maxDeposit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder='Enter maximum deposit'
              min='0'
              step='0.01'
            />
          </div>
        </div>

        {/* ROI */}
        <div className='form-row'>
          <div className='form-control'>
            <label htmlFor='roi'>ROI (%)</label>
            <input
              type='text'
              id='roi'
              value={formik.values.roi}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder=''
            />
          </div>
        </div>

        <div className='btns'>
          <button
            className='red'
            type='button'
            onClick={handleDeleteClick}
            disabled={deleteLoading}
          >
            Delete Trading Plan
            {deleteLoading ? <Spinner /> : null}
          </button>
          <button className='primary' type='submit' disabled={loading}>
            Update Trading Plan
            {loading ? <Spinner /> : null}
          </button>
        </div>
      </form>
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        size='lg'
        className='delete-modal'
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <div className='warning'>
          <h2>Are you sure you want to delete this trading plan?</h2>
        </div>

        <div className='btns'>
          <button className='primary' onClick={handleDeleteConfirm}>
            Yes, I am sure {loading ? <Spinner /> : null}
          </button>
          <button className='grey' onClick={closeDeleteModal}>
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};
